const readElevation = (point) => {
  const value = Number(point?.ele ?? point?.elevation ?? point?.alt ?? point?.altitude);
  return Number.isFinite(value) ? value : null;
};

export const TRACK_ELEVATION_STATS_OPTIONS = {
  minDelta: 5,
  maxDelta: 30,
  maxVerticalSpeed: 0.7,
  minVerticalAllowance: 5,
  smoothingWindow: 5,
};

function smoothElevationPoints(points, opts = {}) {
  const windowSize = Math.max(1, Math.round(Number(opts.smoothingWindow ?? 1)));
  if (!Array.isArray(points) || points.length < 2 || windowSize <= 1) return points;
  const half = Math.floor(windowSize / 2);
  return points.map((point, index) => {
    const vals = [];
    for (let i = index - half; i <= index + half; i += 1) {
      if (i < 0 || i >= points.length) continue;
      const elevation = readElevation(points[i]);
      if (Number.isFinite(elevation)) vals.push(elevation);
    }
    if (!vals.length) return point;
    vals.sort((a, b) => a - b);
    const mid = Math.floor(vals.length / 2);
    const ele =
      vals.length % 2 === 1 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2;
    return { ...point, ele };
  });
}

export function calculateElevationStats(points, opts = {}) {
  const minDelta = Math.max(0, Number(opts.minDelta ?? 1.5));
  const maxDelta = Number.isFinite(Number(opts.maxDelta))
    ? Math.max(minDelta, Number(opts.maxDelta))
    : Infinity;
  const maxVerticalSpeed = Number.isFinite(Number(opts.maxVerticalSpeed))
    ? Math.max(0, Number(opts.maxVerticalSpeed))
    : Infinity;
  const minVerticalAllowance = Math.max(
    minDelta,
    Number(opts.minVerticalAllowance ?? 5)
  );
  let gain = 0;
  let loss = 0;
  let min = Infinity;
  let max = -Infinity;
  let acceptedElevation = null;
  let acceptedTs = null;
  let validPoints = 0;

  for (const point of Array.isArray(points) ? points : []) {
    const elevation = readElevation(point);
    if (!Number.isFinite(elevation)) continue;

    let updateAcceptedElevation = acceptedElevation == null;
    if (acceptedElevation != null) {
      const diff = elevation - acceptedElevation;
      if (Math.abs(diff) > maxDelta) continue;
      const ts = Number(point?.ts ?? point?.time);
      if (
        Number.isFinite(ts) &&
        Number.isFinite(acceptedTs) &&
        ts > acceptedTs &&
        Number.isFinite(maxVerticalSpeed)
      ) {
        const allowance = Math.max(
          minVerticalAllowance,
          ((ts - acceptedTs) / 1000) * maxVerticalSpeed
        );
        if (Math.abs(diff) > allowance) continue;
      }
      if (diff >= minDelta) {
        gain += diff;
        updateAcceptedElevation = true;
      } else if (diff <= -minDelta) {
        loss += -diff;
        updateAcceptedElevation = true;
      }
    }

    validPoints += 1;
    min = Math.min(min, elevation);
    max = Math.max(max, elevation);
    if (updateAcceptedElevation) {
      acceptedElevation = elevation;
      const ts = Number(point?.ts ?? point?.time);
      if (Number.isFinite(ts)) acceptedTs = ts;
    }
  }

  if (validPoints === 0) {
    return { gain: 0, loss: 0, min: null, max: null, validPoints: 0 };
  }

  return { gain, loss, min, max, validPoints };
}

export function calculateTrackElevationStats(points, opts = {}) {
  const options = {
    ...TRACK_ELEVATION_STATS_OPTIONS,
    ...opts,
  };
  const sanitized = sanitizeElevationPoints(points, options);
  const smoothed = smoothElevationPoints(sanitized, options);
  return calculateElevationStats(smoothed, options);
}

export function sanitizeElevationPoints(points, opts = {}) {
  const maxDelta = Math.max(1, Number(opts.maxDelta ?? 30));
  const maxVerticalSpeed = Math.max(0, Number(opts.maxVerticalSpeed ?? 0.7));
  const minVerticalAllowance = Math.max(
    1,
    Number(opts.minVerticalAllowance ?? 5)
  );
  let acceptedElevation = null;
  let acceptedTs = null;

  return (Array.isArray(points) ? points : []).map((point) => {
    const elevation = readElevation(point);
    if (!Number.isFinite(elevation)) return point;

    const ts = Number(point?.ts ?? point?.time);
    if (acceptedElevation != null) {
      const diff = Math.abs(elevation - acceptedElevation);
      const hasElapsed =
        Number.isFinite(ts) && Number.isFinite(acceptedTs) && ts > acceptedTs;
      const elapsedSec = hasElapsed ? (ts - acceptedTs) / 1000 : null;
      const allowance = hasElapsed
        ? Math.max(minVerticalAllowance, elapsedSec * maxVerticalSpeed)
        : maxDelta;
      if (diff > maxDelta || (hasElapsed && diff > allowance)) {
        return { ...point, ele: acceptedElevation };
      }
    }

    acceptedElevation = elevation;
    if (Number.isFinite(ts)) acceptedTs = ts;
    return { ...point, ele: elevation };
  });
}
